// =====================================================================================
// @deprecated — DEAD CODE pending migration of edit.event.modal to <ig-event-frequency-picker>.
//
// Replaced by EventFrequencyPickerComponent at:
//   src/app/common/components/event.frequency.picker/event.frequency.picker.component.ts
//
// Remaining consumer (do not delete this file until migrated):
//   - src/app/modules/shared/components/hub/edit.event.modal/edit.event.modal.component.{ts,html}
// =====================================================================================
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { VisitorEvent } from 'src/app/modules/shared/models/visitor.event';
import { SelectFieldComponent } from '../select.field/select.field.component';
import { EventTimeService } from 'src/app/modules/event-calendar/services/event.time.service';
import { config } from 'src/app/config/config';
import * as dayjs from 'dayjs';
import * as utc from 'dayjs/plugin/utc';
import * as timezone from 'dayjs/plugin/timezone';

/** @deprecated Use EventFrequencyPickerComponent. See file header for migration notes. */
@Component({
  selector: 'ig-date-time-frequency-selector',
  templateUrl: './date.time.frequency.selector.component.html',
  styleUrls: ['./date.time.frequency.selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DateTimeFrequencySelectorComponent implements OnInit {

  @Input() controls: FormArray;
  @Input() timezoneFormatter: string;
  @Input() event?: VisitorEvent;
  @Input() endDateInitOnly: string | null = null;

  @Output() frequencyChange = new EventEmitter<any>();
  readonly GROUP_SIZE = 25;

  frequencyItems = [];
  totalTimes = '';
  dayInWeek = -1;
  weekNo = -1;
  frequency = 'event';
  defaultEventTimes = [];
  message = '';
  selectedTimes = [];

  activeGroupIndex: number | null = null;
  customizeMode = false;
  editingIndex: number | null = null;
  editingFormGroup: FormGroup | null = null;
  isAddingNewEntry = false;

  groupedTimes: { label: string; times: any[]; offset: number }[] = [];
  formattedTimes: string[] = [];

  trackByIndex = (index: number) => index;

  private recomputeFormattedTimes(): void {
    this.formattedTimes = this.selectedTimes.map(t => this.formatTime(t));
  }

  private computeGroupedTimes(): void {
    const groups = [];
    for (let i = 0; i < this.selectedTimes.length; i += this.GROUP_SIZE) {
      const chunk = this.selectedTimes.slice(i, i + this.GROUP_SIZE);
      const first = new Date(chunk[0].startTime);
      const last = new Date(chunk[chunk.length - 1].startTime);
      const fmt = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const fmtFull = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      const sameYear = first.getFullYear() === last.getFullYear();
      groups.push({
        label: sameYear
          ? `${fmt(first)} - ${fmt(last)}, ${first.getFullYear()}`
          : `${fmtFull(first)} - ${fmtFull(last)}`,
        times: chunk,
        offset: i,
      });
    }
    this.groupedTimes = groups;
  }

  toggleGroup(index: number): void {
    this.activeGroupIndex = this.activeGroupIndex === index ? null : index;
    this.customizeMode = false;
    this.editingIndex = null;
    this.editingFormGroup = null;
    this.cdRef.markForCheck();
  }

  enterCustomizeMode(): void {
    this.customizeMode = true;
  }

  exitCustomizeMode(): void {
    this.customizeMode = false;
    this.editingIndex = null;
    this.editingFormGroup = null;
  }

  startEdit(flatIndex: number): void {
    this.isAddingNewEntry = false;
    const time = this.selectedTimes[flatIndex];
    this.editingIndex = flatIndex;
    this.editingFormGroup = this.fb.group({
      startTime: [time.startTime, [Validators.required]],
      endTime: [time.endTime || null],
      isAllDay: [time.isAllDay || false],
      isEndAllDay: [time.isEndAllDay || false],
    });
  }

  saveEdit(flatIndex: number): void {
    if (!this.editingFormGroup) return;
    const v = this.editingFormGroup.value;

    let endTime = v.endTime;

    // When adding a new entry, the end time may be pre-filled from a previous date.
    // If the end date differs from the start date, preserve the end time-of-day
    // but move it to the start date (or next day if it would precede the start time).
    if (this.isAddingNewEntry && v.startTime && endTime && !v.isAllDay && !v.isEndAllDay) {
      const tz = this.event?.timezone || this.timezoneFormatter;
      const formatter = this.eventTimeService.getTimezoneFormatterByTimezone(tz);
      const startDj = dayjs(v.startTime).tz(formatter);
      const endDj = dayjs(endTime).tz(formatter);
      if (!startDj.isSame(endDj, 'day')) {
        let adjusted = startDj.hour(endDj.hour()).minute(endDj.minute()).second(0).millisecond(0);
        if (!adjusted.isAfter(startDj)) {
          adjusted = adjusted.add(1, 'day');
        }
        endTime = adjusted.toISOString();
      }
    }

    if (this.isAddingNewEntry) {
      if (!v.startTime) {
        this.editingFormGroup.get('startTime')?.markAsTouched();
        return;
      }
      this.selectedTimes = [
        ...this.selectedTimes,
        { startTime: v.startTime, endTime, isAllDay: v.isAllDay, isEndAllDay: v.isEndAllDay },
      ];
      this.isAddingNewEntry = false;
    } else {
      this.selectedTimes = [
        ...this.selectedTimes.slice(0, flatIndex),
        { startTime: v.startTime, endTime, isAllDay: v.isAllDay, isEndAllDay: v.isEndAllDay },
        ...this.selectedTimes.slice(flatIndex + 1),
      ];
    }

    this.editingIndex = null;
    this.editingFormGroup = null;
    this.recomputeFormattedTimes();
    this.computeGroupedTimes();
    this.frequency = 'custom';
    this.setFrequency('custom');
    this.frequencyChange.emit({ frequency: 'custom', eventTimes: this.selectedTimes });
  }

  deleteTime(flatIndex: number): void {
    this.selectedTimes = [
      ...this.selectedTimes.slice(0, flatIndex),
      ...this.selectedTimes.slice(flatIndex + 1),
    ];
    this.editingIndex = null;
    this.editingFormGroup = null;
    this.recomputeFormattedTimes();
    this.computeGroupedTimes();
    if (this.activeGroupIndex !== null && this.activeGroupIndex >= this.groupedTimes.length) {
      this.activeGroupIndex = this.groupedTimes.length > 0 ? this.groupedTimes.length - 1 : null;
    }
    this.frequency = 'custom';
    this.setFrequency('custom');
    this.frequencyChange.emit({ frequency: 'custom', eventTimes: this.selectedTimes });
    this.cdRef.markForCheck();
  }

  addTime(): void {
    const isFirstEntry = this.selectedTimes.length === 0;
    const last = this.selectedTimes[this.selectedTimes.length - 1];

    const defaultStart = isFirstEntry
      ? (this.controls.controls[0]?.get('startTime')?.value || null)
      : null;
    const defaultEnd = isFirstEntry
      ? (this.controls.controls[0]?.get('endTime')?.value || null)
      : null;
    const defaultIsAllDay = isFirstEntry
      ? (this.controls.controls[0]?.get('isAllDay')?.value || false)
      : (last?.isAllDay || false);
    const defaultIsEndAllDay = isFirstEntry
      ? (this.controls.controls[0]?.get('isEndAllDay')?.value || false)
      : (last?.isEndAllDay || false);

    this.isAddingNewEntry = true;
    this.editingIndex = null;
    this.editingFormGroup = this.fb.group({
      startTime: [defaultStart, [Validators.required]],
      endTime: [defaultEnd],
      isAllDay: [defaultIsAllDay],
      isEndAllDay: [defaultIsEndAllDay],
    });
    this.customizeMode = true;
    this.frequency = 'custom';
    this.setFrequency('custom');
    if (this.selectedTimes.length >= this.GROUP_SIZE) {
      this.activeGroupIndex = this.groupedTimes.length > 0 ? this.groupedTimes.length - 1 : null;
    }
    this.cdRef.markForCheck();
  }

  cancelEdit(): void {
    this.isAddingNewEntry = false;
    this.editingIndex = null;
    this.editingFormGroup = null;
  }

  setEditingAllDay(value: boolean): void {
    this.editingFormGroup?.get('isAllDay')?.setValue(value);
  }

  setEditingEndAllDay(value: boolean): void {
    this.editingFormGroup?.get('isEndAllDay')?.setValue(value);
  }

  get editingStartControl(): AbstractControl | null {
    return this.editingFormGroup?.get('startTime') ?? null;
  }

  get editingEndControl(): AbstractControl | null {
    return this.editingFormGroup?.get('endTime') ?? null;
  }

  formatTime(time: any): string {
    const tz = this.eventTimeService.getTimezoneFormatterByTimezone(
      this.event?.timezone || this.timezoneFormatter
    );
    const tzOpts: Intl.DateTimeFormatOptions = tz ? { timeZone: tz } : {};
    const start = new Date(time.startTime);
    const dateStr = start.toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', ...tzOpts,
    });
    if (time.isAllDay) {
      if (time.endTime) {
        const endAllDay = new Date(time.endTime);
        const startDay = start.toLocaleDateString('en-US', { year: 'numeric', month: 'numeric', day: 'numeric', ...tzOpts });
        const endDay   = endAllDay.toLocaleDateString('en-US', { year: 'numeric', month: 'numeric', day: 'numeric', ...tzOpts });
        if (startDay !== endDay) {
          const endDateStr = endAllDay.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', ...tzOpts });
          if (time.isEndAllDay) {
            return `${dateStr} - ${endDateStr} · All Day`;
          }
          const endTimeStr = endAllDay.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, ...tzOpts });
          return `${dateStr} All Day - ${endDateStr} · ${endTimeStr}`;
        }
      }
      return `${dateStr} · All Day`;
    }
    const startStr = start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, ...tzOpts });
    if (!time.endTime) return `${dateStr} · ${startStr}`;
    const end = new Date(time.endTime);
    const endStr = end.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, ...tzOpts });
    const startDay = start.toLocaleDateString('en-US', { year: 'numeric', month: 'numeric', day: 'numeric', ...tzOpts });
    const endDay = end.toLocaleDateString('en-US', { year: 'numeric', month: 'numeric', day: 'numeric', ...tzOpts });
    if (startDay !== endDay) {
      const endDateStr = end.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', ...tzOpts });
      return `${dateStr} · ${startStr} - ${endDateStr} · ${endStr}`;
    }
    return `${dateStr} · ${startStr} - ${endStr}`;
  }

  tzNameByFormatter: string;

  @ViewChild('select') select: SelectFieldComponent;

  eventTimeService = inject(EventTimeService);

  constructor(private fb: FormBuilder, private cdRef: ChangeDetectorRef) { }

  ngOnInit(): void {
    this.initFrequencyDropdown();
    this.getTZNameByFormatter();
    this.initializeFrequencyFromEvent();
  }

  private initializeFrequencyFromEvent() {
    if (this.event?.eventTimes && this.event.eventTimes.length > 0) {
      this.selectedTimes = this.event.eventTimes;
      this.recomputeFormattedTimes();
      this.computeGroupedTimes();
    }

    if (this.event?.frequency) {
      const validFrequencies = ['custom', 'daily', 'weekly', 'biweekly', 'monthly', 'last'];

      if (validFrequencies.includes(this.event.frequency)) {
        this.frequency = this.event.frequency as any;
        this.setFrequency(this.frequency);

        if (this.selectedTimes.length > 0 && this.frequency !== 'custom' && this.frequency !== 'event') {
          const timezone = this.event?.timezone || this.timezoneFormatter;
          const first = this.selectedTimes[0].startTime;
          const last = this.selectedTimes[this.selectedTimes.length - 1].startTime;
          this.totalTimes = `${this.selectedTimes.length} Dates ${this.eventTimeService.getDayAndMonth(first, timezone)} - ${this.eventTimeService.getDayAndMonth(last, timezone)}`;
        }
      }
    } else {
      this.frequency = 'custom';
      this.setFrequency('custom');
    }

    this.cdRef.markForCheck();
  }

  addDates() {
    this.controls.push(
      this.fb.group({
        startTime: [null, [Validators.required]],
        endTime: [null, []],
        isAllDay: [false, []],
        isEndAllDay: [false, []],
      })
    );
    this.setFrequency('custom');
    this.frequencySourceChange('custom');
  }

  removeDates(i: number) {
    this.controls.removeAt(i);
    this.setFrequency('custom');
    this.frequencySourceChange('custom');
  }

  datetimePickerChange() {
    this.setFrequency(this.frequency);
    this.frequencySourceChange(this.frequency);
  }

  onAllDayChange(index: number, isAllDay: boolean) {
    const control = this.controls.at(index);
    if (control) {
      control.get('isAllDay').setValue(isAllDay);
    }
  }

  onEndAllDayChange(index: number, isEndAllDay: boolean) {
    const control = this.controls.at(index);
    if (control) {
      control.get('isEndAllDay').setValue(isEndAllDay);
    }
  }

  setFrequency(frequency: string) {
    this.frequency = frequency as any;

    this.frequencyItems.forEach((x) => { x.selected = x.value === frequency; });

    if (this.select) {
      this.select.setSelected(frequency);
    }
  }

  setFrequencyDropdown() {
    if (
      !this.controls.controls ||
      this.controls.controls.length === 0 ||
      !this.controls.controls[0].get('startTime').value
    ) {
      this.message = `⚠️ Please set start date for the event`;
      return;
    }

    this.message = '';

    // Remove 'last' option before updating to avoid duplicates
    this.frequencyItems = this.frequencyItems.filter((x) => x.value !== 'last');

    const start = this.controls.controls[0].get('startTime').value;
    const timezone = this.event?.timezone || this.timezoneFormatter;

    this.updateFrequencyItemsText(start, timezone);
  }

  // DEAD: 170-line switch/case replaced by OccurrenceGeneratorFactory + RRuleGenerator.
  // The flaw fixed in the new flow: this method reads start/end from the form's FormArray,
  // which loses the original event range when processEventTimes() prunes past dates.
  /** @deprecated Use OccurrenceService.expand(rule) on a RecurrenceRule. */
  frequencySourceChange(target) {
    this.frequency = target.value || target;

    let start:string;
    let end:string;

    if (this.controls.controls.length > 0) {
      start = this.controls.controls[0].get('startTime').value;
      end = this.controls.controls[this.controls.controls.length - 1].get('endTime').value || this.controls.controls[0].get('endTime').value;
    }

    const isAllDay = !!this.controls.controls[0]?.get('isAllDay')?.value;
    const isEndAllDay = !!this.controls.controls[0]?.get('isEndAllDay')?.value;

    let msg = '';
    if (!start && !end) msg = 'start and end date';
    if (!start && end) msg += 'start date';
    if (start && !end) msg += 'end date';

    if (msg && this.frequency !== 'event' && this.frequency !== 'custom') {
      this.message = `⚠️ Please set ${msg} for the event`;
      return;
    }
    this.message = '';

    const timezone = this.event?.timezone || this.timezoneFormatter;

    let eventTimes = [];

    let days = [];
    let diff = 0;

    switch (this.frequency) {
      case 'daily':
        diff = this.eventTimeService.diff(start, end);
        this.totalTimes = `${diff + 1} Dates ${this.eventTimeService.getDayAndMonth(
          start,
          timezone
        )} -
          ${this.eventTimeService.getDayAndMonth(end, timezone)}`;

        for (let i = 0; i <= diff; i++) {
          let startTime = this.eventTimeService.addHours(new Date(start), 24 * i);

          let time = this.adjustForDaylightSaving(start, end, startTime);
          this.setTimes(time.startTime, time.endTime, eventTimes, isAllDay, isEndAllDay);
        }
        break;
      case 'weekly':
        diff = this.eventTimeService.diff(start, end, 'weeks');
        this.totalTimes = `${diff + 1} Dates
          ${this.eventTimeService.getDayAndMonthAndYear(start, timezone)}
          -
          ${this.eventTimeService.getDayAndMonthAndYear(end, timezone)}`;

        for (let i = 0; i <= diff; i++) {
          let startTime = this.eventTimeService.addHours(new Date(start), 24 * 7 * i);

          let time = this.adjustForDaylightSaving(start, end, startTime);
          this.setTimes(time.startTime, time.endTime, eventTimes, isAllDay, isEndAllDay);
        }
        break;
      case 'biweekly':
        diff = this.eventTimeService.diff(start, end, 'biweekly');
        this.totalTimes = `${diff + 1} Dates ${this.eventTimeService.getDayAndMonthAndYear(
          start,
          timezone
        )} - ${this.eventTimeService.getDayAndMonthAndYear(end, timezone)}`;

        for (let i = 0; i <= diff; i++) {
          let startTime = this.eventTimeService.addHours(new Date(start), 24 * 14 * i);
          let time = this.adjustForDaylightSaving(start, end, startTime);
          this.setTimes(time.startTime, time.endTime, eventTimes, isAllDay, isEndAllDay);
        }
        break;
      case 'monthly':
        days = this.eventTimeService.getSameDayInWeek(
          start,
          end,
          this.dayInWeek,
          this.weekNo,
          timezone
        );
        this.totalTimes = `${days.length} Dates ${this.eventTimeService.getDayAndMonthAndYear(
          start,
          timezone
        )} - ${this.eventTimeService.getDayAndMonthAndYear(end, timezone)}`;
        days.forEach((x) => {
          this.setTimes(x, end, eventTimes, isAllDay, isEndAllDay);
        });
        break;
      case 'last':
        days = this.eventTimeService.getLastWeekDays(
          start,
          end,
          this.dayInWeek,
          timezone
        );
        this.totalTimes = `${days.length} Dates ${this.eventTimeService.getDayAndMonthAndYear(
          start,
          timezone
        )} - ${this.eventTimeService.getDayAndMonthAndYear(end, timezone)}`;
        days.forEach((x) => {
          this.setTimes(x, end, eventTimes, isAllDay, isEndAllDay);
        });
        break;
      default:
        break;
    }

    this.selectedTimes = eventTimes;
    this.recomputeFormattedTimes();
    this.computeGroupedTimes();
    this.cdRef.markForCheck();

    this.frequencyChange.emit({
      frequency: this.frequency,
      eventTimes: eventTimes,
    });
  }

  public getSelectedTimes() {
    return this.selectedTimes;
  }

  public getSelectedTimesTooltip(): string {
    if (!this.selectedTimes || this.selectedTimes.length === 0) {
      return '';
    }

    const timezone = this.event?.timezone || this.timezoneFormatter;

    return this.selectedTimes.map(time => {
      const startDate = new Date(time.startTime);
      const endDate = time.endTime ? new Date(time.endTime) : null;

      // Format time to match the form field display (12-hour format with AM/PM)
      const startTimeStr = startDate.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });

      const endTimeStr = endDate ? endDate.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      }) : '';

      // Format date to match the form field display
      const dateStr = startDate.toLocaleDateString('en-US', {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric'
      });

      return `${dateStr} ${startTimeStr}${endTimeStr ? ' - ' + endTimeStr : ''}`;
    }).join('\n');
  }

  // DEAD: hand-rolled DST correction. rrule.js (used by RRuleGenerator) handles DST
  // correctly via wall-clock anchoring; no manual scrossDaylight ST/DT shift is needed.
  /** @deprecated Replaced by wallClockUTC/wallUTCToReal in rrule.generator.ts. */
  private adjustForDaylightSaving(start, end, nextDayStart) {
    const timezone = this.event?.timezone || this.timezoneFormatter;
    const formatter = this.eventTimeService.getTimezoneFormatterByTimezone(timezone);
    const startTimezone = this.eventTimeService.getTimezonecodeWithTimeSaving(start, formatter);
    const nextDayTimezone = this.eventTimeService.getTimezonecodeWithTimeSaving(
      nextDayStart,
      formatter
    );
    if (startTimezone !== nextDayTimezone) {
      // Only nextDayStart drifts from the fixed-hour addition across the DST boundary; `end` is re-read fresh in setTimes() so shifting it here double-corrects.
      if (nextDayTimezone.indexOf('ST') >= 0) {
        nextDayStart = this.eventTimeService.addHours(nextDayStart, 1);
      } else {
        nextDayStart = this.eventTimeService.addHours(nextDayStart, -1);
      }
    }
    return {
      startTime: nextDayStart,
      endTime: end,
    };
  }

  private setTimes(start, end, eventTimes, isAllDay = false, isEndAllDay = false) {
    const tz = this.event?.timezone || this.timezoneFormatter;
    const formatter = this.eventTimeService.getTimezoneFormatterByTimezone(tz);

    if (formatter) {
      const startDj = dayjs(start).tz(formatter);
      const endDj = dayjs(end).tz(formatter);
      const endTime = startDj.hour(endDj.hour()).minute(endDj.minute()).second(0).millisecond(0);
      eventTimes.push({
        startTime: startDj.toISOString(),
        endTime: endTime.toISOString(),
        isAllDay,
        isEndAllDay,
      });
    } else {
      const startTime = new Date(start);
      const endDate = new Date(end);
      const endTime = new Date(
        startTime.getFullYear(), startTime.getMonth(), startTime.getDate(),
        endDate.getHours(), endDate.getMinutes(), endDate.getSeconds(), endDate.getMilliseconds()
      );
      eventTimes.push({
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString(),
        isAllDay,
        isEndAllDay,
      });
    }
  }

  private initFrequencyDropdown() {
    // Initialize base frequency items
    this.frequencyItems = [
      {
        text: 'Custom',
        value: 'custom',
        selected: true,
      },
      {
        text: 'Daily',
        value: 'daily',
      },
      {
        text: 'Weekly',
        value: 'weekly',
      },
      {
        text: 'Biweekly',
        value: 'biweekly',
      },
      {
        text: 'Monthly',
        value: 'monthly',
      },
    ];

    const start = this.controls.controls[0].get('startTime').value;
    if (!start) {
      return;
    }

    const timezone = this.event?.timezone || this.timezoneFormatter;
    this.updateFrequencyItemsText(start, timezone);
  }

  private getTZNameByFormatter() {
    this.tzNameByFormatter = this.eventTimeService.getTZNameByFormatter(
      this.timezoneFormatter
    );
  }

  /**
   * Updates the text labels for frequency dropdown items based on the start time
   * @param start - The start time to calculate frequency labels from
   * @param timezone - The timezone to use for calculations
   */
  private updateFrequencyItemsText(start: string, timezone: string): void {
    this.dayInWeek = this.eventTimeService.getDayOfWeek(start, timezone);
    const dayText = config.dayOfWeek[this.dayInWeek].name;

    // Update weekly text
    const weekly = this.frequencyItems.first((x) => x.value === 'weekly');
    if (weekly) {
      weekly.text = `Weekly on ${dayText}`;
    }

    // Update biweekly text
    const biWeekly = this.frequencyItems.first((x) => x.value === 'biweekly');
    if (biWeekly) {
      biWeekly.text = `Every Other ${dayText} (starts from ${this.eventTimeService.getDayAndMonth(
        start,
        timezone
      )})`;
    }

    // Update monthly text
    const monthly = this.frequencyItems.first((x) => x.value === 'monthly');
    if (monthly) {
      const no = this.eventTimeService.getWeekDayNo(start, timezone);

      if (!no.isLast) {
        this.weekNo = no.no;
        monthly.text = `Monthly on the ${no.text} ${dayText}`;
        monthly.value = 'monthly';
      } else if (no.isLast && no.text === 'last') {
        monthly.text = `Monthly on the last ${dayText}`;
        monthly.value = 'last';
      } else if (no.isLast && no.text !== 'last') {
        this.weekNo = no.no;
        monthly.text = `Monthly on the ${no.text} ${dayText}`;
        monthly.value = 'monthly';

        this.frequencyItems.push({
          value: 'last',
          text: `Monthly on the last ${dayText}`,
        });
      }
    }
  }
}
